/**
 * Chart settings entry points (ADR-0026; TradingView "Chart settings…"). The dialog itself lives in
 * `ChartSettingsDialog.tsx` and is rendered by the chart module (`ChartPanel`), which owns the pane
 * specs it edits; everything else opens it through here:
 *
 * - the gear in each pane header (the module header for a single chart),
 * - the command palette ("Chart: Chart settings…", most recently used pane),
 * - **`openChartSettings(paneKey, tab?)`** — for the chart right-click menu (another branch). The
 *   pane key is the chart's `chartKey` (`${panelId}/${paneId}`, the same key drawings use), which
 *   `ChartWidget` receives as a prop.
 *
 * Defaults: a pane without its own settings shows the user's saved default (`settings/main` →
 * `chart_settings_default`, synced like indicator templates) or the factory settings.
 */
import { useSettings } from "@helm/core";
import { DEFAULT_CHART_SETTINGS, sanitizeChartSettings, type ChartSettings } from "@helm/chart";
import { settingsSync } from "../session";

/** Dialog tabs (TradingView order; "trading" is a placeholder until chart trading exists). */
export type ChartSettingsTab = "symbol" | "status" | "scales" | "appearance" | "trading";

type Opener = (tab?: ChartSettingsTab) => void;

const openers = new Map<string, Opener>();
/** Pane keys, most recently used last. */
const recent: string[] = [];

function touch(key: string): void {
  const i = recent.indexOf(key);
  if (i >= 0) recent.splice(i, 1);
  recent.push(key);
}

/** A chart pane offers its settings dialog (ChartPanel); returns the unregister function. */
export function registerChartSettingsTarget(paneKey: string, open: Opener): () => void {
  openers.set(paneKey, open);
  if (!recent.includes(paneKey)) recent.unshift(paneKey);
  return () => {
    if (openers.get(paneKey) !== open) return;
    openers.delete(paneKey);
    const i = recent.indexOf(paneKey);
    if (i >= 0) recent.splice(i, 1);
  };
}

/** The pane was used (clicked / focused / made active): the palette command targets it next. */
export function markChartSettingsTarget(paneKey: string): void {
  if (openers.has(paneKey)) touch(paneKey);
}

/** Chart panes that can open settings (palette `when`). */
export function chartSettingsTargets(): number {
  return openers.size;
}

/**
 * Open the chart settings dialog of pane `paneKey` (`${panelId}/${paneId}`, `ChartWidget`'s
 * `chartKey`) on `tab`; without a key, the most recently used pane. False when no such pane is
 * mounted.
 */
export function openChartSettings(paneKey?: string | null, tab?: ChartSettingsTab): boolean {
  const key = paneKey ?? recent.at(-1);
  const open = key === undefined ? undefined : openers.get(key);
  if (!key || !open) return false;
  touch(key);
  open(tab);
  return true;
}

/** The saved default (sanitised), or the factory settings. */
export function chartSettingsDefault(raw: unknown): ChartSettings {
  return raw === null || raw === undefined ? DEFAULT_CHART_SETTINGS : sanitizeChartSettings(raw);
}

/** The user's saved default ("Save as default"), live, plus its setters. */
export function useChartSettingsDefault(): {
  base: ChartSettings;
  saved: boolean;
  save: (s: ChartSettings) => void;
  clear: () => void;
} {
  const raw = useSettings((s) => s.settings.chart_settings_default);
  return {
    base: chartSettingsDefault(raw),
    saved: raw !== null && raw !== undefined,
    save: (s) => {
      void settingsSync.update({ chart_settings_default: s });
    },
    clear: () => {
      void settingsSync.update({ chart_settings_default: null });
    },
  };
}

/** A pane's stored settings over the default: what the pane shows. */
export function paneChartSettings(raw: unknown, base: ChartSettings): ChartSettings {
  return raw === null || raw === undefined ? base : sanitizeChartSettings(raw, base);
}
